import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Checkbox } from '../ui/checkbox';
import { COURTS, HOURS, pad, apiFetch, isBlock, hourOf } from './tennisCalendarUtils';

const selectCls = 'w-full h-10 rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-900';

const MemberPicker = ({ members, value, onChange }) => {
  const [q, setQ] = useState('');
  const list = useMemo(() => {
    const s = q.toLowerCase();
    return members
      .filter((m) => m.is_approved)
      .filter((m) => !s || `${m.first_name} ${m.last_name} ${m.email} ${m.phone || ''}`.toLowerCase().includes(s))
      .slice(0, 50);
  }, [members, q]);
  const selected = members.find((m) => m.id === value);

  useEffect(() => {
    if (value && !list.some((m) => m.id === value)) onChange('');
  }, [list, value, onChange]);

  return (
    <div className="space-y-2">
      <Label>Medlem</Label>
      <Input data-testid="booking-member-search" placeholder="Søk navn, e-post eller telefon…" value={q} onChange={(e) => setQ(e.target.value)} />
      <div className="max-h-44 overflow-y-auto rounded-md border border-slate-300 divide-y divide-slate-100" role="listbox" data-testid="booking-member-select">
        {list.map((m) => (
          <button
            key={m.id}
            type="button"
            role="option"
            aria-selected={m.id === value}
            data-testid={`booking-member-option-${m.id}`}
            onClick={() => onChange(m.id)}
            className={`w-full text-left px-3 py-2 text-sm flex items-center justify-between gap-2 transition-colors ${m.id === value ? 'bg-[#CCFF00]/40 text-black font-semibold' : 'hover:bg-slate-50 text-slate-700'}`}
          >
            <span className="truncate min-w-0">{m.first_name} {m.last_name}</span>
            <span className="text-xs text-slate-500 truncate min-w-0 hidden sm:inline">{m.email}</span>
          </button>
        ))}
        {list.length === 0 && <div className="px-3 py-3 text-sm text-slate-500">Ingen treff</div>}
      </div>
      <p className="text-xs text-slate-500" data-testid="booking-member-selected">
        {selected ? <>Valgt: <strong className="text-slate-900">{selected.first_name} {selected.last_name}</strong></> : 'Velg et medlem i listen'}
      </p>
    </div>
  );
};

const TimeFields = ({ form, setForm, maxHours, onTimeChange }) => (
  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
    <div className="space-y-2">
      <Label htmlFor="bk-date">Dato</Label>
      <Input id="bk-date" data-testid="booking-date" type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
    </div>
    <div className="space-y-2">
      <Label>Bane</Label>
      <select data-testid="booking-court" className={selectCls} value={form.court_number} onChange={(e) => setForm({ ...form, court_number: Number(e.target.value) })}>
        {COURTS.map((c) => <option key={c} value={c}>Bane {c}</option>)}
      </select>
    </div>
    <div className="space-y-2">
      <Label>Start</Label>
      <select data-testid="booking-time" className={selectCls} value={form.time_slot} onChange={(e) => onTimeChange(e.target.value)}>
        {HOURS.map((h) => <option key={h} value={pad(h)}>{pad(h)}</option>)}
      </select>
    </div>
    <div className="space-y-2">
      <Label>Varighet</Label>
      <select data-testid="booking-duration" className={selectCls} value={form.duration_hours} onChange={(e) => setForm({ ...form, duration_hours: Number(e.target.value) })}>
        {Array.from({ length: maxHours }, (_, i) => i + 1).map((n) => <option key={n} value={n}>{n} {n === 1 ? 'time' : 'timer'}</option>)}
      </select>
    </div>
  </div>
);

export const BookingDialog = ({ open, onClose, initial, members, onSaved }) => {
  const editing = Boolean(initial?.id);
  const block = initial && isBlock(initial);
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm({
      member_id: initial?.member_id || '',
      court_number: initial?.court_number || 1,
      date: initial?.date || '',
      time_slot: initial?.time_slot || '10:00',
      duration_hours: initial?.duration_hours || 1,
      note: initial?.note || '',
      send_email: true,
    });
  }, [open, initial, members]);

  const maxHours = Math.max(1, 21 - hourOf(form.time_slot || '10:00'));
  const setMemberId = useCallback((member_id) => setForm((f) => ({ ...f, member_id })), []);
  const setTime = (time_slot) => setForm((f) => ({ ...f, time_slot, duration_hours: Math.min(f.duration_hours, Math.max(1, 21 - hourOf(time_slot))) }));

  const save = async () => {
    setBusy(true);
    try {
      if (editing) {
        const { send_email, ...rest } = form;
        await apiFetch(`/admin/bookings/${initial.id}`, { method: 'PUT', body: JSON.stringify(block ? { ...rest, member_id: undefined } : rest) });
        toast.success(block ? 'Blokkering oppdatert' : 'Booking oppdatert');
      } else {
        await apiFetch('/admin/bookings', { method: 'POST', body: JSON.stringify(form) });
        toast.success('Booking opprettet');
      }
      onSaved();
      onClose();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  const cancelBooking = async () => {
    if (!window.confirm(block ? 'Fjerne blokkeringen?' : 'Kansellere denne bookingen?')) return;
    setBusy(true);
    try {
      await apiFetch(`/admin/bookings/${initial.id}`, { method: 'DELETE' });
      toast.success(block ? 'Blokkering fjernet' : 'Booking kansellert');
      onSaved();
      onClose();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg w-[calc(100vw-1.5rem)] sm:w-full grid-cols-[minmax(0,1fr)] max-h-[92vh] overflow-y-auto bg-white text-slate-900" data-testid="booking-dialog">
        <DialogHeader>
          <DialogTitle>{block ? 'Blokkering' : editing ? 'Endre booking' : 'Ny booking'}</DialogTitle>
          <DialogDescription>
            {block ? `Blokkert: ${initial.member_name}` : editing ? `Booket av ${initial.member_name}${initial.member_phone ? ` · ${initial.member_phone}` : ''}` : 'Opprett en booking på vegne av et medlem. Admin er ikke begrenset av medlemsgrensene.'}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          {!block && <MemberPicker members={members} value={form.member_id || ''} onChange={setMemberId} />}
          <TimeFields form={form} setForm={setForm} maxHours={maxHours} onTimeChange={setTime} />
          <div className="space-y-2">
            <Label htmlFor="bk-note">Notat (kun synlig for admin)</Label>
            <Input id="bk-note" data-testid="booking-note" value={form.note || ''} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder="F.eks. betalt kontant, kurs…" />
          </div>
          {!editing && (
            <label className="flex items-center gap-2 text-sm">
              <Checkbox data-testid="booking-send-email" checked={form.send_email} onCheckedChange={(v) => setForm({ ...form, send_email: Boolean(v) })} />
              Send bekreftelse på e-post til medlemmet
            </label>
          )}
        </div>
        <DialogFooter className="gap-2 sm:justify-between">
          {editing ? (
            <Button variant="outline" data-testid="booking-cancel-btn" className="border-red-300 text-red-600 hover:bg-red-50" onClick={cancelBooking} disabled={busy}>
              {block ? 'Fjern blokkering' : 'Kanseller booking'}
            </Button>
          ) : <span />}
          <Button data-testid="booking-save-btn" className="bg-[#CCFF00] text-black hover:bg-[#b8e600] font-bold" onClick={save} disabled={busy || (!block && !form.member_id)}>
            {busy ? 'Lagrer…' : editing ? 'Lagre endringer' : 'Opprett booking'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const BlockDialog = ({ open, onClose, initialDate, onSaved }) => {
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) setForm({ court_numbers: [1, 2], date: initialDate || '', start_time: '08:00', end_time: '21:00', reason: 'Vedlikehold', cancel_conflicts: false });
  }, [open, initialDate]);

  const toggleCourt = (c) =>
    setForm({ ...form, court_numbers: form.court_numbers.includes(c) ? form.court_numbers.filter((x) => x !== c) : [...form.court_numbers, c] });

  const save = async () => {
    setBusy(true);
    try {
      const res = await apiFetch('/admin/blocks', { method: 'POST', body: JSON.stringify(form) });
      toast.success(`${res.blocks.length} blokkering(er) opprettet${res.cancelled_bookings.length ? `, ${res.cancelled_bookings.length} booking(er) kansellert` : ''}`);
      onSaved();
      onClose();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg w-[calc(100vw-1.5rem)] sm:w-full grid-cols-[minmax(0,1fr)] max-h-[92vh] overflow-y-auto bg-white text-slate-900" data-testid="block-dialog">
        <DialogHeader>
          <DialogTitle>Blokker tid</DialogTitle>
          <DialogDescription>Sperr baner for vedlikehold, arrangementer eller klubbaktiviteter. Medlemmer ser tiden som opptatt.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Baner</Label>
            <div className="flex gap-4">
              {COURTS.map((c) => (
                <label key={c} className="flex items-center gap-2 text-sm">
                  <Checkbox data-testid={`block-court-${c}`} checked={form.court_numbers?.includes(c)} onCheckedChange={() => toggleCourt(c)} /> Bane {c}
                </label>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-2">
              <Label htmlFor="bl-date">Dato</Label>
              <Input id="bl-date" data-testid="block-date" type="date" value={form.date || ''} onChange={(e) => setForm({ ...form, date: e.target.value })} />
            </div>
            <div className="space-y-2">
              <Label>Fra</Label>
              <select data-testid="block-start" className={selectCls} value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })}>
                {HOURS.map((h) => <option key={h} value={pad(h)}>{pad(h)}</option>)}
              </select>
            </div>
            <div className="space-y-2">
              <Label>Til</Label>
              <select data-testid="block-end" className={selectCls} value={form.end_time} onChange={(e) => setForm({ ...form, end_time: e.target.value })}>
                {HOURS.map((h) => <option key={h} value={pad(h + 1)}>{pad(h + 1)}</option>)}
              </select>
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="bl-reason">Årsak (vises i kalenderen)</Label>
            <Input id="bl-reason" data-testid="block-reason" value={form.reason || ''} onChange={(e) => setForm({ ...form, reason: e.target.value })} />
          </div>
          <label className="flex items-start gap-2 text-sm">
            <Checkbox data-testid="block-cancel-conflicts" checked={form.cancel_conflicts} onCheckedChange={(v) => setForm({ ...form, cancel_conflicts: Boolean(v) })} className="mt-0.5" />
            <span>Kanseller eksisterende bookinger som kolliderer <span className="text-slate-500">(ellers avvises blokkeringen ved kollisjon)</span></span>
          </label>
        </div>
        <DialogFooter>
          <Button data-testid="block-save-btn" className="bg-slate-900 text-white hover:bg-slate-700 font-bold" onClick={save} disabled={busy || !form.date || !form.court_numbers?.length}>
            {busy ? 'Lagrer…' : 'Blokker'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
